import React from 'react'
import {HomepageCarouselGridProps} from './types'
import {ProductCategoryCarousel} from 'hec/components/product-category-carousel'
import ImageTextCarouselItem from 'hec/components/image-text-carousel/image-text-item'
import {cn} from '@/lib/utils'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    ContentButtonNavigation,
    navButtonTypes
} from '@/components/ui/carousel'

const HomepageCarouselGrid: React.FC<HomepageCarouselGridProps> = (props) => {
    const {
        addedToCartPodId,
        slidesVisible,
        resultCount = 10,
        productTileBorderColor,
        showPeek,
        slidePadding,
        productCategory,
        ButtonNavigationColor,
        showNavigationArrows,
        iconsHeaderText = 'Popular Categories',
        productCarouselHeaderText = '',
        enableHeroOverlap = false,
        items = [],
        carouselCtaText,
        enableBasicView,
        external,
        external_link,
        link,
        attributes
    } = props

    const category = productCategory?.options?.category

    return (
        <div
            {...attributes}
            className={cn(
                'flex flex-col-reverse lg:flex-row gap-5 justify-center items-stretch mx-auto w-[calc(100%-16px)] p-0 max-w-[1440px]',
                {'-mt-[60px] z-[9]': enableHeroOverlap},
                attributes?.className
            )}
        >
            <div
                className={cn(
                    'left-container flex flex-col justify-center items-center w-full lg:w-[68%] h-auto min-h-[450px] bg-white rounded-[12px] shadow-[0_4px_20px_0_rgba(0,0,0,0.22)]',
                    'lg:h-[auto] xl:h-[auto]'
                )}
            >
                <ProductCategoryCarousel
                    categoryId={category}
                    resultCount={resultCount}
                    enableHeadline={true}
                    headlineText={productCarouselHeaderText}
                    addedToCartPodId={addedToCartPodId}
                    productTileBorderColor={productTileBorderColor}
                    showPeek={showPeek?.onDesktop ?? false}
                    showPeekMobile={showPeek?.onMobile ?? false}
                    slidesVisible={(slidesVisible?.onDesktop || '4') as any}
                    slidesVisibleMobile={(slidesVisible?.onMobile || '2') as any}
                    slidePadding={slidePadding?.onDesktop}
                    slidePaddingMobile={slidePadding?.onMobile}
                    ButtonNavigationColor={ButtonNavigationColor}
                    enableNavigationArrows={true}
                    showNavigationArrowsMobile={showNavigationArrows?.onMobile ?? true}
                    showNavigationArrowsDesktop={showNavigationArrows?.onDesktop ?? false}
                    ctaText={carouselCtaText}
                    enableBasicView={enableBasicView}
                    ctaHref={external ? external_link?.value?.data?.url : link?.value?.data?.url}
                    ctaExternal={external}
                    className="h-full w-full"
                    data-testid="ProductCategoryCarousel"
                />
            </div>
            <div
                className={cn(
                    'right-container flex flex-col gap-2 lg:gap-4 items-center justify-center lg:justify-start w-full lg:w-[32%] min-h-[auto] lg:min-h-[440px] bg-white rounded-[12px] shadow-[0_4px_20px_0_rgba(0,0,0,0.22)] p-[18px] lg:p-[28px]',
                    'lg:h-[auto] xl:h-[auto]'
                )}
            >
                <div className="icon-container-header mb-4">
                    <h2 className="text-emphasis leading-[1.2] text-[26px] lg:text-[36px] font-medium text-center">
                        {iconsHeaderText}
                    </h2>
                </div>
                {/* Desktop Grid View */}
                <div className="hidden lg:grid grid-cols-3 gap-2 xl:gap-6">
                    {items?.map((item, index) => (
                        <ImageTextCarouselItem
                            key={`image-text-item-${index}`}
                            image={item.image}
                            text={item.text}
                            external={item.external}
                            external_link={item.external_link}
                            link={item.link}
                            contentType="grid"
                        />
                    ))}
                </div>
                {/* Mobile Carousel View */}
                <div className="lg:hidden w-full">
                    <Carousel
                        opts={{loop: true, align: 'start'}}
                        className="h-full flex flex-col justify-center"
                    >
                        <div className="flex items-center gap-2">
                            <CarouselPrevious
                                className="static translate-x-0 translate-y-0"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="carousel-Previous"
                            />
                            <CarouselContent className="flex-1 -ml-2">
                                {items?.map((item, index) => (
                                    <CarouselItem
                                        key={`image-text-carousel-item-${index}`}
                                        slidesVisible={(slidesVisible?.onDesktop || '3.25') as any}
                                        slidesVisibleMobile={'3'}
                                        showPeek={false}
                                        showPeekMobile={false}
                                        slidePadding={slidePadding?.onDesktop}
                                        slidePaddingMobile={slidePadding?.onMobile}
                                        className="sm:basis-1/4"
                                        data-testid="imageTextCarousel-CarouselItem"
                                    >
                                        <ImageTextCarouselItem
                                            image={item.image}
                                            text={item.text}
                                            external={item.external}
                                            external_link={item.external_link}
                                            link={item.link}
                                            contentType="grid"
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselNext
                                className="static translate-x-0 translate-y-0"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="carousel-Next"
                            />
                        </div>
                        <ContentButtonNavigation
                            buttonType={navButtonTypes.FlatButton}
                            buttonColor={ButtonNavigationColor}
                            className="mt-2"
                            data-testid="imageTextCarousel-ContentButtonNavigation"
                        />
                    </Carousel>
                </div>
            </div>
        </div>
    )
}

export default HomepageCarouselGrid
